/**
 * React 18+ Compatibility Polyfill for @twilio/video-room-monitor
 *
 * The @twilio/video-room-monitor package (v1.0.1) uses ReactDOM.render() which was
 * removed in React 18. This polyfill re-adds the render() method using the new
 * createRoot API to maintain backward compatibility.
 *
 * This file must be imported before any code that uses @twilio/video-room-monitor.
 */
import ReactDOM from 'react-dom';
import { createRoot, Root } from 'react-dom/client';

// Store roots for cleanup purposes
const rootMap = new WeakMap<Element, Root>();

// Add the legacy render method to ReactDOM for backward compatibility
if (!(ReactDOM as any).render) {
  (ReactDOM as any).render = (
    element: React.ReactElement,
    container: Element | DocumentFragment | null,
    callback?: () => void
  ) => {
    if (!container) {
      console.error('ReactDOM.render polyfill: container is null');
      return null;
    }

    // Get or create a root for this container
    let root = rootMap.get(container as Element);
    if (!root) {
      root = createRoot(container);
      rootMap.set(container as Element, root);
    }

    // Render the element
    root.render(element);

    // Call the callback if provided (React 18 doesn't support the callback natively)
    if (callback) {
      setTimeout(callback, 0);
    }

    // Return the container for backward compatibility
    return container;
  };
}

// Add unmountComponentAtNode for cleanup
if (!(ReactDOM as any).unmountComponentAtNode) {
  (ReactDOM as any).unmountComponentAtNode = (container: Element) => {
    const root = rootMap.get(container);
    if (root) {
      root.unmount();
      rootMap.delete(container);
      return true;
    }
    return false;
  };
}

export {};
